/**
 * Webhooks API Service
 * Gerencia endpoints para receber dados externos
 */

import api from './api';

export interface Webhook {
  id: string;
  name: string;
  description?: string;
  url: string;
  method: 'POST' | 'GET' | 'PUT' | 'PATCH';
  headers?: Record<string, string>;
  secret?: string;
  status: 'active' | 'inactive' | 'paused';
  organization_id: string;
  created_at: string;
  updated_at: string;
  last_triggered_at?: string;
  trigger_count: number;
  success_count: number;
  error_count: string;
}

export interface WebhookPayload {
  event_type: string;
  data: Record<string, any>;
  timestamp: string;
  webhook_id: string;
  signature?: string;
}

export interface WebhookLog {
  id: string;
  webhook_id: string;
  request_id: string;
  status: 'success' | 'error';
  status_code?: number;
  request_headers: Record<string, string>;
  request_body: string;
  response_headers?: Record<string, string>;
  response_body?: string;
  error_message?: string;
  processing_time_ms: number;
  created_at: string;
  created_by?: string;
}

export interface WebhookTestResult {
  success: boolean;
  status_code: number;
  response: any;
  processing_time_ms: number;
  error?: string;
}

/**
 * Webhooks API
 */
export const webhooksApi = {
  /**
   * Listar todos os webhooks
   */
  async list(params?: {
    status?: 'active' | 'inactive' | 'paused';
    search?: string;
  }): Promise<Webhook[]> {
    const response = await api.get('/webhooks/', { params });
    return response.data;
  },

  /**
   * Obter webhook por ID
   */
  async get(id: string): Promise<Webhook> {
    const response = await api.get(`/webhooks/${id}/`);
    return response.data;
  },

  /**
   * Criar novo webhook
   */
  async create(data: {
    name: string;
    description?: string;
    url: string;
    method?: 'POST' | 'GET' | 'PUT' | 'PATCH';
    headers?: Record<string, string>;
    secret?: string;
  }): Promise<Webhook> {
    const response = await api.post('/webhooks/', data);
    return response.data;
  },

  /**
   * Atualizar webhook
   */
  async update(id: string, data: Partial<Webhook>): Promise<Webhook> {
    const response = await api.patch(`/webhooks/${id}/`, data);
    return response.data;
  },

  /**
   * Deletar webhook
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/webhooks/${id}/`);
  },

  /**
   * Ativar webhook
   */
  async activate(id: string): Promise<Webhook> {
    const response = await api.post(`/webhooks/${id}/activate/`);
    return response.data;
  },

  /**
   * Pausar webhook
   */
  async pause(id: string): Promise<Webhook> {
    const response = await api.post(`/webhooks/${id}/pause/`);
    return response.data;
  },

  /**
   * Testar webhook
   */
  async test(id: string, payload?: any): Promise<WebhookTestResult> {
    const response = await api.post(`/webhooks/${id}/test/`, payload);
    return response.data;
  },

  /**
   * Obter URL pública do webhook
   */
  async getPublicUrl(id: string): Promise<{ url: string; secret?: string }> {
    const response = await api.get(`/webhooks/${id}/public-url/`);
    return response.data;
  },

  /**
   * Regenerar secret do webhook
   */
  async regenerateSecret(id: string): Promise<{ secret: string }> {
    const response = await api.post(`/webhooks/${id}/regenerate-secret/`);
    return response.data;
  },

  /**
   * Listar logs de um webhook
   */
  async listLogs(id: string, params?: {
    status?: 'success' | 'error';
    limit?: number;
    offset?: number;
  }): Promise<WebhookLog[]> {
    const response = await api.get(`/webhooks/${id}/logs/`, { params });
    return response.data;
  },

  /**
   * Obter detalhes de um log específico
   */
  async getLog(id: string, logId: string): Promise<WebhookLog> {
    const response = await api.get(`/webhooks/${id}/logs/${logId}/`);
    return response.data;
  },

  /**
   * Reenviar webhook com base em log
   */
  async retryFromLog(id: string, logId: string): Promise<WebhookTestResult> {
    const response = await api.post(`/webhooks/${id}/logs/${logId}/retry/`);
    return response.data;
  },

  /**
   * Obter estatísticas do webhook
   */
  async getStats(id: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{
    total_triggers: number;
    success_count: number;
    error_count: number;
    success_rate: number;
    avg_processing_time_ms: number;
    last_triggered_at?: string;
    daily_stats: Array<{
      date: string;
      count: number;
      success_count: number;
      error_count: number;
    }>;
  }> {
    const response = await api.get(`/webhooks/${id}/stats/`, { params });
    return response.data;
  },

  /**
   * Exportar webhook como JSON
   */
  async exportWebhook(id: string): Promise<Blob> {
    const response = await api.get(`/webhooks/${id}/export/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Importar webhook
   */
  async importWebhook(file: File): Promise<Webhook> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/webhooks/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default webhooksApi;
